<template>
  <div class="column full-height">
    <div
      v-if="showQueueBar"
      class="col-auto full-weight col-auto bg-grey-2 label-queue"
    >
      <div class="row justify-center">
        <span class="text-blue-grey-10 q-my-xs">{{ queueName }}</span>
      </div>
    </div>

    <AttendanceHeader
      :id="attendance.contact"
      :contact="attendance.contactName"
      :created-at="attendance.createdAt"
      :finished-at="attendance.hangupAt"
      :direction="attendance.direction"
      :channel="attendance.channel"
      :attendance="attendance"
      @on-finish="handleHangup"
      @change-view="$emit('change-view', $event)"
      :is-preview="isPreview"
    />

    <BarAttendanceFrequency
      v-if="!isFinished && attendance.direction === 'IN'"
      :attendance-id="attendance.id"
    />

    <ContactAttendancesHistory
      v-if="showBody && isHistoryTab"
      v-show="isHistoryTab"
      :attendance="attendance"
    />

    <q-scroll-area
      v-if="showBody"
      v-show="isOngoingTab"
      class="col column justify-center items-center"
    >
      <div class="row">
        <div class="col q-py-xs q-pl-xs">
          <template v-if="isFinished">
            <div class="row justify-around items-end">
              <div class="column">
                <DetailsCall
                  v-if="!isAdmin"
                  :phone="attendance.contact"
                  :call-id="attendanceId"
                  :queue-id="queueId"
                  :agent-id="agent.id"
                  :direction="attendance.direction"
                  :created-at="attendance.createdAt"
                />
                <div
                  v-if="s3AudioUrl"
                  :class="['row audio-container', isAdmin ? 'q-mt-lg' : '']"
                >
                  <AudioPlayer
                    :url="s3AudioUrl"
                    :show-download-btn="true"
                    :name="fileNameToLocal"
                    mime="audio/mpeg"
                    @handleDiscardRecord="handleDiscardRecord"
                    :show-link-btn="false"
                    control-width="width: 380px"
                  />
                </div>
              </div>
            </div>
          </template>

          <div
            v-if="!isFinished"
            class="column items-center"
          >
            <div
              class="col-auto row items-center justify-center"
              style="max-width: 370px"
            >
              <BtnControl
                label="<b>Tirar<br>Mute</b>"
                color="red-8"
                @on-click="handleMute"
                v-if="isOngoing && attendance.isMuted"
                tooltip="O cliente não está escutando!<br>Clique aqui para voltar a conversar com ele!"
              >
                <template #icon>
                  <DesmuteIcon />
                </template>
              </BtnControl>

              <BtnControl
                :disabled="attendance.onHold"
                v-if="isOngoing && !attendance.isMuted && !isListenExtension"
                label="Colocar<br>em Mute"
                @on-click="handleMute"
                tooltip="Clique aqui para colocar o cliente no <b>MUTE</b> sem ele saber!"
              >
                <template #icon>
                  <MicrophoneIcon />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && attendance.onHold"
                label="<b>Tirar da<br>Espera</b>"
                color="red-8"
                @on-click="handleHold"
                tooltip="O cliente está escutando uma música de espera!<br>Clique aqui para voltar a conversar com ele!"
              >
                <template #icon>
                  <UnwaitIcon />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && !attendance.onHold && !isListenExtension"
                label="Colocar<br>em Espera"
                @on-click="handleHold"
                tooltip="Clique aqui para colocar o cliente em espera!<br>Ele irá ficar ouvindo uma música de espera.<br>Você também não vai escutar o cliente enquanto ele estiver na espera!"
              >
                <template #icon>
                  <PauseIcon />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && !isRecording"
                label="Iniciar gravação"
                color="black"
                @on-click="handleStartRecord"
                tooltip="Começar a Gravar"
              >
                <template #icon>
                  <q-icon
                    name="mdi-record-circle-outline"
                  />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && isRecording"
                label="Gravando"
                color="black"
                @on-click="handleStopRecord"
                tooltip="Parar a Gravação"
              >
                <template #icon>
                  <q-icon name="mdi-play-pause" />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing"
                label="Teclado"
                :tooltip="isListenExtension ? 'Tecla 6 -> Falar com Agente e Cliente<br>Tecla 5 -> Falar com Agente<br>Tecla 4 -> Voltar para apenas escutar' : 'Abrir o teclado, pois preciso acionar uma tecla/dígito.'"
              >
                <template #icon>
                  <DialerIcon />
                </template>

                <q-menu>
                  <KeyboardCall :attendance-id="attendanceId" />
                </q-menu>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && !showTransferOptions"
                color="green"
                label="Transferir"
                tooltip="Opções de Transferência"
                @on-click="showTransferOptions = true"
              >
                <template #icon>
                  <TransferIcon />
                </template>
              </BtnControl>

              <BtnControl
                label="<b>Conferência<br>em andamento</b>"
                color="primary"
                v-if="isOngoing && callInConference"
                @on-click="handleConference"
                tooltip="Esta chamada está em conferência!<br>Clique para desfazer a conferência ou encerre essa chamada para removê-la."
              >
                <template #icon>
                  <q-icon name="mdi-account-group" />
                </template>
              </BtnControl>

              <BtnControl
                v-if="isOngoing && !callInConference && !isListenExtension"
                label="Conferência"
                color="primary"
                :disabled="!hasVoiceAttendances"
                @on-click="handleConference"
                tooltip="Criar uma conferência com todas as chamadas em andamento."
              >
                <template #icon>
                  <q-icon name="mdi-account-group" />
                </template>
              </BtnControl>

              <BtnControl
                v-if="showAttemptButton"
                label="<b>Atender</b>"
                color="positive"
                @on-click="handleAccept"
                tooltip="Atender essa chamada"
              >
                <template #icon>
                  <AttemptIcon />
                </template>
              </BtnControl>

              <BtnControl
                :label="hangupLabel"
                color="red-8"
                @on-click="handleHangup"
                :tooltip="hangupHelp"
              >
                <template #icon>
                  <HangupIcon />
                </template>
              </BtnControl>
            </div>
            <div
              class="full-width flex justify-center"
              v-if="(isRecording || recordUrl)"
            >
              <div class="flex justify-center">
                <div class="row justify-around items-center cursor-pointer button-container">
                  <div
                    v-if="isOngoing && isRecording"
                    class="row justify-between items-center full-width"
                  >
                    <q-btn
                      @click="handleStopRecord"
                      class=" play-pause-btn flex items-center justify-center"
                    >
                      <q-icon
                        color="primary"
                        name="mdi-square"
                        size="xs"
                      />
                      <q-tooltip>Parar a gravação</q-tooltip>
                    </q-btn>

                    <div>
                      <q-spinner-bars
                        color="primary"
                        size="3em"
                      />

                      <q-spinner-bars
                        color="primary"
                        size="3em"
                      />

                      <q-spinner-bars
                        color="primary"
                        size="3em"
                      />

                      <q-spinner-bars
                        color="primary"
                        size="3em"
                      />

                      <q-spinner-bars
                        color="primary"
                        size="3em"
                      />
                    </div>

                    <TimerWrapper
                      v-if="startRecordTime"
                      style="color:#165A9F"
                      :created-at="startRecordTime"
                      :finished-at="stopRecordTime"
                    />
                  </div>
                  <div
                    v-if="!isRecording && recordUrl"
                    class="row audio-container"
                  >
                    <AudioPlayer
                      :url="recordUrl"
                      :show-download-btn="true"
                      :name="fileNameToLocal"
                      mime="audio/mpeg"
                      @handleDiscardRecord="handleDiscardRecord"
                      :show-link-btn="false"
                      control-width="width: 380px"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AttendanceTransfer
          v-if="isOngoing && showTransferOptions"
          @on-transfer-number="handlerTransferNumber"
          @close-transfer-options="showTransferOptions = false"
        />
      </div>
    </q-scroll-area>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import BtnControl from '@/components/BtnControl'
import KeyboardCall from '@/components/KeyboardCall'
import { confirmTransfer } from '@/libs/transferUtils'
import AttendanceTransfer from '@/components/AttendanceTransfer'
import AttendanceHeader from '@/components/AttendanceHeader'
import DetailsCall from '@/components/DetailsCall'
import TransferIcon from '@/assets/icons/transfer.svg'
import UnwaitIcon from '@/assets/icons/unwait.svg'
import PauseIcon from '@/assets/icons/pause.svg'
import HangupIcon from '@/assets/icons/hangup.svg'
import DialerIcon from '@/assets/icons/dialer.svg'
import AttemptIcon from '@/assets/icons/attemptIcon.svg'
import DesmuteIcon from '@/assets/icons/desmute.svg'
import MicrophoneIcon from '@/assets/icons/microphone.svg'
import { VOICE_CHANNEL } from '@/constants/channels'
import BarAttendanceFrequency from './BarAttendanceFrequency'
import ContactAttendancesHistory from './ContactAttendancesHistory'
import { CONNECTED, HOLD, CONFERENCE } from '@/constants/callStatus'
import AudioPlayer from '@/components/AudioPlayer'
import TimerWrapper from '@/components/TimerWrapper'
import moment from 'moment-timezone'
import events from '@/libs/events'

const busEvents = events()
const ipcRenderer = window.ipcRenderer

export default {
  components: {
    BtnControl,
    DetailsCall,
    AttendanceHeader,
    AttendanceTransfer,
    AudioPlayer,

    KeyboardCall,

    UnwaitIcon,
    DialerIcon,
    DesmuteIcon,
    AttemptIcon,
    TransferIcon,
    MicrophoneIcon,
    PauseIcon,
    HangupIcon,
    BarAttendanceFrequency,
    ContactAttendancesHistory,
    TimerWrapper

  },

  props: {
    attendanceId: {},
    tab: {},
    isPreview: {
      type: Boolean,
      default: false
    }
  },

  data () {
    const showTransferOptions = false
    return {
      showTransferOptions,
      startRecordTime: null,
      stopRecordTime: null
    }
  },

  mounted () {
    if (this.isRecording) this.startRecordTime = this.startRecordTimestamp
  },

  computed: {
    ...mapGetters({
      customer: 'settingsModule/customer',
      previewQueue: 'attendanceModule/previewQueue'
    }),

    ...mapState({
      attendance ({ attendanceModule }) {
        const { attendanceId } = this
        const { current, history } = attendanceModule

        return current[attendanceId] ?? history[attendanceId] ?? {}
      },

      callInConference ({ attendanceModule }) {
        return attendanceModule.conference.includes(this.attendanceId)
      },

      agent: ({ agentModule }) => agentModule?.session?.agent ?? {},
      hasVoiceAttendances: ({ attendanceModule }) => {
        return Object.values(attendanceModule.current).filter(({ channel }) => channel === VOICE_CHANNEL).length > 1
      },

      group ({ attendanceModule }) {
        const attendances = { ...attendanceModule.current }
        delete attendances[this.attendanceId]
        return Object.keys(attendances)
      },
      extension: ({ settingsModule }) => settingsModule.provision?.extension
    }),

    s3AudioUrl () {
      console.log('ATTENDANCE', this.attendance)
      if (this.attendance?.recordS3Url) {
        return this.attendance.recordS3Url
      }

      if (this.attendance?.recordUrl) {
        return this.attendance.recordUrl
      }

      if (this.attendance?.id && !this.attendance.fetchingS3Url && !this.attendance.recordS3UrlFetched) {
        this.$store.commit('attendanceModule/UPDATE_ATTENDANCE', {
          id: this.attendanceId,
          recordS3UrlFetched: true
        })

        this.$store.dispatch('attendanceModule/fetchRecordingS3Url', {
          attendanceId: this.attendanceId
        })
      }

      return null
    },

    isListenExtension () {
      const contact = String(this.attendance.contact || '')
      return contact.includes('*99*')
    },

    queueId () {
      if (!this.agent.id) return this.attendance?.queue
      const isDirectionOUT = this.attendance.direction === 'OUT'
      return isDirectionOUT ? this.agent.defaultQueueId : this.attendance.queue || this.attendance.queueId
    },

    hangupLabel () {
      const canReject = !this.isOngoing && ['IN', 'AUTO'].includes(this.attendance.direction)
      if (canReject) return 'Rejeitar'
      return this.showAttemptButton ? 'Desligar' : '<b>Desligar</b>'
    },

    recordUrl () {
      return this.attendance?.recordUrl
    },

    showAttemptButton () {
      return !this.isOngoing && ['IN', 'AUTO'].includes(this.attendance.direction)
    },

    hangupHelp () {
      const canReject = !this.isOngoing && ['IN', 'AUTO'].includes(this.attendance.direction)
      if (canReject) return 'Rejeitar esse atendimento'
      return 'Desligar essa chamada'
    },

    queueName () {
      const queueDirection = this.isPreview ? this.previewQueue : this.queueId
      if (!queueDirection) return null

      const queue = this.membership.find(({ queueId }) => queueId === queueDirection)
      return queue?.queueName ?? queueDirection
    },

    membership () {
      return Array.isArray(this.agent.membership) ? [...this.agent.membership] : []
    },

    isFinished () {
      return Boolean(this.attendance?.hangupAt)
    },

    isOngoing () {
      return [CONNECTED, HOLD, CONFERENCE].includes(this.attendance.status)
    },

    isRecording () {
      return Boolean(this.attendance.isRecording)
    },

    startRecordTimestamp () {
      return this.attendance.startRecordTimestamp
    },

    showBody () {
      return Boolean(this.attendance?.id)
    },

    showQueueBar () {
      return !String(this.attendance?.contact).includes('*99*')
    },

    attendanceFrequency () {
      return this.attendance?.attendanceFrequency
    },

    isOngoingTab () {
      return this.tab === 'current'
    },

    isHistoryTab () {
      return this.tab === 'history'
    },

    fileNameToLocal () {
      const date = moment(this.attendance.createdAt).format('YYYYMMDD')
      const hour = moment(this.attendance.createdAt).format('HHmmss')
      const calleridNum = this.attendance.contact.replace(/\s+/g, '_')
      if (this.isAdmin) return `${date}_${hour}_Ramal_${this.extension}_${calleridNum}.mp3`
      const formattedAgentName = this.agent.name.replace(/\s+/g, '_')
      const formattedQueueName = this.queueName.replace(/\s+/g, '_')
      const name = `${date}_${hour}_${calleridNum}_Agente_${formattedAgentName.trim()}_${this.agent.id}_Fila_${formattedQueueName.trim()}.mp3`
      return name
    }
  },

  methods: {
    ...mapActions({
      accept: 'sipModule/accept',
      hangup: 'sipModule/hangup',
      hold: 'sipModule/hold',
      unhold: 'sipModule/unhold',
      mute: 'sipModule/mute',
      startRecord: 'attendanceModule/startRecord',
      stopRecord: 'attendanceModule/stopRecord',
      toggleMicMute: 'attendanceModule/toggleMicMute',
      pauseRecord: 'attendanceModule/pauseRecord',
      resumeRecord: 'attendanceModule/resumeRecord',
      unmute: 'sipModule/unmute',
      createConference: 'sipModule/createConference',
      destroyConference: 'sipModule/destroyConference',
      transferEntryNumber: 'sipModule/transferEntryNumber',

      DTMFAudioPlay: 'sipModule/DTMFAudioPlay',
      sendDTMF: 'sipModule/sendDTMF'
    }),

    handleDigit (key) {
      if (!key) return
      const vm = this
      vm.DTMFAudioPlay(key)
      vm.sendDTMF({ tone: key, attendanceId: vm.attendanceId })
    },

    handleStartRecord () {
      const vm = this
      this.startRecordTime = Date.now()
      if (this.stopRecordTime) this.stopRecordTime = null
      vm.startRecord(vm.attendanceId)
    },

    handleStopRecord () {
      const vm = this
      this.stopRecordTime = Date.now()
      vm.stopRecord(vm.attendanceId)
    },

    handlerTransferNumber (entryNumber) {
      confirmTransfer(this, entryNumber, this.attendance)
    },

    handleHangup () {
      this.hangup(this.attendanceId)
    },

    handleAccept () {
      const id = this.attendanceId
      this.accept(id)
      busEvents.emit('cancel-attendance-offer', { id })
      ipcRenderer.send('cancel-attendance-offer', { id })
    },

    handleMute () {
      if (this.attendance.isMuted) {
        if (this.isRecording) this.toggleMicMute({ attendanceId: this.attendanceId, isMuted: false })
        return this.unmute(this.attendanceId)
      }
      if (this.isRecording) {
        this.toggleMicMute({ attendanceId: this.attendanceId, isMuted: true })
      }
      this.mute(this.attendanceId)
    },

    handleHold () {
      const vue = this
      if (vue.attendance.onHold) {
        if (this.isRecording) vue.resumeRecord(vue.attendanceId)
        return vue.unhold(vue.attendanceId)
      }
      if (vue.attendance.isMuted) vue.unmute(vue.attendanceId)
      if (this.isRecording) vue.pauseRecord(vue.attendanceId)
      vue.hold(vue.attendanceId)
    },

    handleConference () {
      const action = this.callInConference ? this.destroyConference : this.createConference
      action({ attendanceId: this.attendanceId, sessions: this.group })
    },

    handleDiscardRecord () {
      this.startRecordTime = null
      this.stopRecordTime = null
      this.attendance.recordUrl = null
    }
  }
}
</script>

<style lang="sass" scoped>
  .label-queue
    font-size: 13px
    font-weight: 500
    line-height: 17px

  .play-pause-btn
    border-radius: 30px
    height: 40px
    width: 40px
    &:hover
      background-color: rgb(22,90,159, 0.2)

  .button-container
    borderhandleDradius: 30px
    height: 50px
    width: 350px

  .mdi-square, .mdi:before
    font-size: 20px

  .audio-player
    border: 1px solid #d9d9d9
    border-radius: 10px
    padding: 3px
    width: 390px

</style>
